import { ChevLeftIcon } from "@/assets/icons";

import { twMerge } from "tailwind-merge";

import DatasetAccordion from "./DatasetAccordion";
import { InputWithClear } from "./InputWithClear";
import ScoringOptionSidebar from "./ScoringOptionSidebar";
import { DataWeightSettingsValue } from "./useDataWeightSettings";
import WeightSummarySection from "./WeightSummarySection";

/**
 * Full-screen two-column Data Weight panel (advanced mode when the
 * isAdvanceDataWeightOptionsEnabled feature flag is ON).
 */
export default function AdvancedDataWeightPanel({
  state,
  actions,
  derived,
  onClose
}: DataWeightSettingsValue & { onClose: () => void }) {
  const { selectedScoringOption, intercept } = state;
  const {
    setSelectedScoringOption,
    setMode,
    setIntercept,
    resetAllWeights,
    handleSaveSetting
  } = actions;
  const {
    availableDatasets,
    totalAttributes,
    hasWeightValuesChanged,
    isAllAttributeWeightZero,
    isAllDatasetWeightZero,
    isAdvancedNormalizationInvalid
  } = derived;

  return (
    <>
      <div className="fixed inset-0 z-[100] bg-black/30" aria-hidden="true" />
      <div
        className={twMerge(
          "z-[101] font-sans border-gainsboro bg-white rounded-lg",
          "w-[calc(100vw-1.5rem)] h-[calc(100vh-1.5rem)]",
          "flex flex-col pt-4 fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs"
        )}
      >
        {/* Header */}
        <div className="flex flex-row items-start justify-between px-4">
          <h2 className="text-txtHeading font-bold text-[20px]">
            Advanced Settings Data Weight
          </h2>
        </div>

        {/* Two-column body */}
        <div className="flex flex-1 min-h-0 gap-4 px-4 pb-4 pt-3">
          {/* Left sidebar */}
          <aside className="flex w-[328px] shrink-0 flex-col">
            <ScoringOptionSidebar
              selectedScoringOption={selectedScoringOption}
              setSelectedScoringOption={setSelectedScoringOption}
              readOnly
            />
            <div className="mt-auto pt-4">
              <button
                type="button"
                onClick={() => setMode("simple")}
                className="flex w-full items-center gap-1 bg-transparent border-0 text-teal-500 text-sm font-semibold mb-2"
              >
                <ChevLeftIcon className="w-4 h-4 fill-teal-500" />
                <span>Back to Simple Settings</span>
              </button>
              <WeightSummarySection
                availableDatasets={availableDatasets}
                totalAttributes={totalAttributes}
                hasWeightValuesChanged={hasWeightValuesChanged}
                selectedScoringOption={selectedScoringOption}
                isAllAttributeWeightZero={isAllAttributeWeightZero}
                isAllDatasetWeightZero={isAllDatasetWeightZero}
                isSaveDisabled={isAdvancedNormalizationInvalid}
                resetAllWeights={resetAllWeights}
                handleSaveSetting={() => {
                  // In advanced mode an invalid save returns false — keep the
                  // panel open so the revealed red borders stay visible.
                  if (handleSaveSetting()) onClose();
                }}
                className="p-0"
              />
            </div>
          </aside>

          {/* Right content */}
          <div className="flex flex-1 min-w-0 flex-col gap-4 overflow-y-auto custom-scrollbar scrollbar-overlay">
            {/* Intercept (UI only) */}
            <div className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-txtHeading">
                Intercept (optional)
              </span>
              <InputWithClear
                value={intercept}
                onChange={setIntercept}
                allowNegative
                inputMode="decimal"
                placeholder="e.g 234000"
                ariaLabel="Intercept"
                className="rounded-xl bg-white text-txtHeading"
              />
              <span className="text-[10px] text-txtBody">
                Baseline constant from the training model. Added to every grid
                score to reflect predicted absolute value, not just ranking.
              </span>
            </div>

            {/* Dataset Attributes Weight Section */}
            <DatasetAccordion
              showAdvancedColumns={true}
              highlightWeightAdjusted
              state={state}
              actions={actions}
              derived={derived}
            />
          </div>
        </div>
      </div>
    </>
  );
}
